import { Label } from "~/components/ui/label";
import { useSite } from "~/contexts";
import { Ratings } from "~/hec/components/product/ratings";
import type { HecProduct } from "~/hec/types/product";
import type { C_ProductInfo } from "~/lib/app.types";
import type { SerializeFrom } from "~/lib/types";
import { useRootLayoutData } from "~/root";
import { useFormatPrice } from "~/shop/utils/price";
import { decodeMasterTilePrice } from "~/shop/utils/scapi-price";

import { BundleItems } from "../components/bundle-items";
import {
  type CompareToData,
  CompareToMessage,
} from "../components/compare-to-message";
import { HybridCategoryGrids } from "../components/hybrid-pdp";
import { ProductBuyForm } from "../components/product-buy-form";
import { ProductImages } from "../components/product-images";
import { PromotionCallouts } from "../components/promotion-callouts";
import type { ProductPageData } from "../loader/product-detail-loader";
import { renderBrandRow } from "../utils/render-brand-row";

/**
 * HybridPDP — site-neutral compact PDP variant used when the product is
 * non-orderable or `?hybrid=true` is in the URL. Renders a compact image
 * + buy column on top, then up to 3 category mini-PLPs below.
 *
 * Composed by the dispatcher in `product-details.tsx` (and `WDH-PDP/wdh-pdp.tsx`).
 * Extracted from the inline block originally at `product-details.tsx:346-486`
 * as part of SFCC-11038 PDP variant refactor.
 */
export function HybridPDP({
  product,
  master,
  productId,
  inStockWarningLevel,
  hybridGrids,
}: {
  product: SerializeFrom<HecProduct>;
  master: ProductPageData["master"];
  productId: string;
  inStockWarningLevel: ProductPageData["inStockWarningLevel"];
  hybridGrids: ProductPageData["hybridGrids"];
}) {
  const formatPrice = useFormatPrice();
  const { site } = useSite();
  // BM-pref-driven free-shipping badge threshold (root loader); null = hidden.
  const freeShippingThreshold = useRootLayoutData().freeShipping.badgeThreshold;
  const p = product as Record<string, unknown>;
  // SCAPI omits `price` on a nothing-orderable master, which is precisely what
  // this layout renders — so the hook price is the hot path, not a fallback
  // (production shows $7.99 for 40209m). `salePrice` first: on a markdown the
  // decoder puts the "was" in `price`, and non-masters reach here too.
  // ponytail: static, like production — this label is outside `SelectedVariant`.
  const decodedPrice = decodeMasterTilePrice(p).decoded;
  const displayPrice =
    (p.price as number | undefined) ??
    decodedPrice?.salePrice ??
    decodedPrice?.price;

  return (
    <div className="mx-auto mt-4 w-full md:max-w-[1335px]">
      <div className="flex flex-col gap-6 md:flex-row md:gap-8">
        <div className="w-full md:w-5/12">
          <ProductImages
            product={product as SerializeFrom<C_ProductInfo>}
            compact
          />
        </div>
        <div className="flex w-full flex-col gap-3 md:w-7/12">
          <ProductBuyForm
            product={product as SerializeFrom<C_ProductInfo>}
            productId={productId}
            master={master}
            wrapOptions
            inStockWarningLevel={inStockWarningLevel}
          >
            <div className="space-y-2">
              <h1 className="font-grotesque text-xl font-medium text-primary md:text-[1.75rem]">
                {(product as { name?: string }).name}
              </h1>
              {renderBrandRow(product)}
              {(() => {
                const rating = Number(p.c_averageRating ?? 0);
                const reviewCount = Number(p.c_totalReviews ?? 0);
                return rating > 0 || reviewCount > 0 ? (
                  <Ratings
                    rating={rating}
                    reviewCount={reviewCount}
                    siteId={site.id}
                    size="small"
                  />
                ) : null;
              })()}
              <PromotionCallouts
                promotions={(p as HecProduct).productPromotions}
                testId="pdp-promotion-callouts-hybrid"
              />
              {displayPrice != null && (
                <div className="pt-1">
                  <Label
                    className="font-grotesque text-2xl font-medium text-product-price"
                    data-testid="hybrid-pdp-price"
                  >
                    {formatPrice(displayPrice)}
                  </Label>
                </div>
              )}
              <CompareToMessage
                data={p.compareToData as CompareToData | undefined}
                siteId={site.id}
                compact
              />
              {/* Free shipping message (SFRA: price >= FreeShippingMinPrice,
                  gated on EnableProductFreeShipping — BM prefs via root loader) */}
              {freeShippingThreshold != null &&
                (displayPrice ?? 0) >= freeShippingThreshold && (
                  <p className="text-sm font-medium text-accent">
                    This item ships free!
                  </p>
                )}
            </div>
          </ProductBuyForm>
          {/* Description below ATC — matches production layout */}
          {!!p.c_siteShortDescription && (
            <p className="mt-3 text-sm leading-relaxed text-foreground">
              {p.c_siteShortDescription as string}
            </p>
          )}
          {/* Bundle contained items (SFCC-11826) — below the buy block,
              mirroring the full PDP. Renders null for non-bundles. */}
          <BundleItems product={product as HecProduct} variant="pdp" />
        </div>
      </div>
      <HybridCategoryGrids hybridGrids={hybridGrids} />
    </div>
  );
}
